/* eslint-disable no-debugger */
import React, { useRef, useState } from 'react';
import { Grid, GridColumn as Column, GridToolbar, GridPdfExportButton } from '@progress/kendo-react-grid';
import "@progress/kendo-theme-default/dist/all.css";
import { saveGridPDF } from '@progress/kendo-react-pdf';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';
import style from './KendoReport.module.css';
import PopUpModal from '../../dialogs/PopUpModal';

const ReportGrid = ({
  reportData,
  isLoaded,
  columns = [],
  title = "Report",
  hasSubReportColumn = false,
  generateSubReportColumns,
  hasRowSubReportData = () => false,
  usePagination = false,
  pageSize = undefined,
  enablePdfExport = true,
  enableExcelExport = true,
  gridHeight = '400px',
  detailArrayName,
  detailReportTitle,
  detailGridHeight,
  excelColumns = [],
  exportDetailData = true
}) => {
  const _export = useRef(null);
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Add unique IDs to data
  const reportDataWithKey = React.useMemo(() => {
    if (!reportData || reportData.length === 0) {
      return [];
    }
    return reportData.map((item, index) => ({
      ...item,
      _gridId: index + 1,
    }));
  }, [reportData]);

  // Pagination setup
  const total = reportData.length;
  const [page, setPage] = React.useState({ skip: 0, take: pageSize });

  const pageChange = (event) => {
    setPage(event.page);
  };

  // Export handlers
  const handlePdfExport = async (event) => {
    const callBack = () => {
      console.log('PDF Exported');
    };
    setTimeout(async () => {
      saveGridPDF(
        event.target,
        {
          fileName: `${title.replace(/\s+/g, '')}.pdf`,
          margin: '1cm'
        },
        callBack
      );
    }, 250);
  };

  const excelExport = () => {
    setLoading(true);
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  // Custom cell for hierarchy
  const CustomCell = (props) => {
    if (!hasSubReportColumn) return null;

    return <td {...props.tdProps}>{props.dataItem[detailArrayName]?.length > 0 && props.children}</td>;
  };

  // Detail component for sub-reports
  const DetailComponent = (props) => {
    if (!hasSubReportColumn) return null;

    const dataItem = props.dataItem;
    const subReportsKey = Object.keys(dataItem).find(key => key.toLowerCase() === detailArrayName.toLowerCase());

    if (!subReportsKey || !dataItem[subReportsKey] || !Array.isArray(dataItem[subReportsKey])) {
      return <div style={{ padding: '10px' }}>No data available</div>;
    }

    const subReportData = dataItem[subReportsKey];

    if (subReportData.length === 0) {
      return <div style={{ padding: '10px' }}>No data available</div>;
    }

    const subReportColumns = generateSubReportColumns;

    return (
      <div style={{ padding: '10px' }}>
        <h4>{detailReportTitle}</h4>
        <Grid
          data={subReportData}
          style={{ height: detailGridHeight }}
          sortable={{
              mode: 'single'
            }}
          autoProcessData={true}
        >
          {subReportColumns.map((column) => (
            <Column
              className={style['col-test']}
              headerClassName={style['header-col']}
              key={column.field}
              field={column.field}
              title={column.title}
              width={column.width}
              format={column.format}
              cell={column.cell}
            />
          ))}
        </Grid>
      </div>
    );
  };

  const gridData = () => {
    const gridData = usePagination
    ? reportDataWithKey.slice(page.skip, page.skip + page.take)
    : reportDataWithKey;

    return gridData;
  }

  const excelData = () => {
    let flatExcelArray = [];

    reportDataWithKey.forEach(item => {
      let excelObject = {};
      excelObject = {
        ...item
      };

      if (
        detailArrayName === undefined || 
        !item[detailArrayName] || 
        item[detailArrayName].length === 0 || 
        exportDetailData === false
      ) {
        flatExcelArray.push(excelObject);
        return;
      }

      item[detailArrayName]?.forEach(subItem => {
        Object.entries(subItem).forEach(([key, value]) => {
          excelObject[`${detailArrayName}_${key}`] = value;
        });

        const newExcelObject = { ...excelObject };
        delete newExcelObject[detailArrayName];
        flatExcelArray.push(newExcelObject);
      });
    });

    return flatExcelArray;
  }

  return (
    <>
      {!isLoaded ? (
        <div className="k-loading-mask">
          <span className="k-loading-text">Loading</span>
          <div className="k-loading-image"></div>
          <div className="k-loading-color"></div>
        </div>
      ) : (
        <>
          <Grid
            style={{ height: gridHeight }}
            className={`${style['grid-test']} ${style['k-grid-norecords-template']}`}
            data={gridData()}
            dataItemKey="_gridId"
            ref={gridRef}
            filterable={true}
            sortable={{
              mode: 'single'
            }}
            autoProcessData={usePagination ? false : true}
            pageable={usePagination}
            onPageChange={usePagination ? pageChange : undefined}
            pageSize={usePagination ? pageSize : undefined}
            total={usePagination ? total : undefined}
            {...(usePagination ? page : {})}
            pdf={{
              data: reportData
            }}
            onPdfExport={enablePdfExport ? handlePdfExport : undefined}
            loading={!isLoaded}
            detail={hasSubReportColumn ? DetailComponent : undefined}
            cells={hasSubReportColumn ? {
              hierarchy: {
                data: CustomCell
              }
            } : undefined}
            onRowRender={hasSubReportColumn ? (e) => {
              const originalItem = reportData.find(item =>
                JSON.stringify(item) === JSON.stringify({ ...e.dataItem, hasDetailRow: undefined, _gridId: undefined })
              );
              if (!hasRowSubReportData(originalItem)) {
                const detailCell = e.element.querySelector('.k-hierarchy-cell');
                if (detailCell) {
                  detailCell.style.display = 'none';
                }
              }
            } : undefined}
          >
            <GridToolbar className={style['k-toolbar']}>
              {enableExcelExport && (
                <Button style={{ backgroundColor: '#00B3E4', width: '130px', height: '35px', fontSize: '16px' }} title="Export Excel" themeColor={'primary'} type="button" onClick={excelExport}>
                  Export to Excel
                </Button>
              )}
              {/* {enablePdfExport &&
                <GridPdfExportButton style={{ backgroundColor: '#00B3E4', width: '130px', height: '35px', fontSize: '16px' }} themeColor={'primary'} />
              } */}
              <div style={{ width: '800px', fontSize: '28px', textAlign: 'center', color: '#041E42' }}>
                {title}
              </div>
            </GridToolbar>
            {columns.map((column) => (
              <Column
                className={style['col-test']}
                headerClassName={style['header-col']}
                key={column.field}
                width={column.width}
                id={column.id || column.field}
                field={column.field}
                title={column.title}
                format={column.format}
                cells={column.cells}
              />
            ))}
          </Grid>
          <ExcelExport
            data={excelData()}
            ref={_export}
            onExportComplete={() => setLoading(false)}
          >
            {excelColumns.map((column) => (
              <Column
                key={column.field}
                id={column.id || column.field}
                field={column.field}
                title={column.title}
              />
            ))}
          </ExcelExport>
          <PopUpModal
            displayPopUp={loading}
            maxWidth={250}
            title="Loading..."
            widthPct={50} />
        </>
      )}
    </>
  );
};

export default ReportGrid;
